import config from "@/config";
import type { ConfigProps } from "@/types/config";

export type BillingInterval = "weekly" | "monthly" | "annual";

export type StripePlan = ConfigProps["stripe"]["plans"][number];

export function planMatchesPriceId(plan: StripePlan, priceId: string): boolean {
  return (
    plan.priceId === priceId ||
    plan.weeklyPriceId === priceId ||
    plan.yearlyPriceId === priceId
  );
}

export function findPlanByPriceId(
  priceId: string | null | undefined
): StripePlan | undefined {
  if (!priceId) return undefined;
  return config.stripe.plans.find((plan) => planMatchesPriceId(plan, priceId));
}

export function billingIntervalForPriceId(
  plan: StripePlan,
  priceId: string
): BillingInterval {
  if (plan.yearlyPriceId === priceId) return "annual";
  if (plan.weeklyPriceId === priceId) return "weekly";
  return "monthly";
}

export function priceIdForInterval(
  plan: StripePlan,
  interval: BillingInterval
): string {
  if (interval === "weekly" && plan.weeklyPriceId) return plan.weeklyPriceId;
  if (interval === "annual" && plan.yearlyPriceId) return plan.yearlyPriceId;
  return plan.priceId;
}

export function displayPriceForInterval(
  plan: StripePlan,
  interval: BillingInterval
): number {
  if (interval === "weekly") return plan.weeklyPrice ?? plan.price;
  if (interval === "annual") return plan.yearlyPrice ?? plan.price * 12;
  return plan.price;
}

export function formatUsd(amount: number): string {
  return amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
}

/** Normalized weekly cost for comparison across billing intervals. */
export function weeklyEquivalentPrice(
  plan: StripePlan,
  interval: BillingInterval
): number {
  if (interval === "weekly") return plan.weeklyPrice ?? plan.price;
  if (interval === "monthly") return plan.price / 4.33;
  return (plan.yearlyPrice ?? plan.price * 12) / 52;
}

/** Normalized monthly cost for comparison across billing intervals. */
export function monthlyEquivalentPrice(
  plan: StripePlan,
  interval: BillingInterval
): number {
  if (interval === "monthly") return plan.price;
  if (interval === "weekly") return (plan.weeklyPrice ?? plan.price) * 4.33;
  return (plan.yearlyPrice ?? plan.price * 12) / 12;
}

export function billingCadenceLabel(interval: BillingInterval): string {
  if (interval === "weekly") return "Billed weekly";
  if (interval === "monthly") return "Billed monthly";
  return "Billed annually";
}

export function billableAmountLabel(
  plan: StripePlan,
  interval: BillingInterval
): string {
  const amount = formatUsd(displayPriceForInterval(plan, interval));
  if (interval === "weekly") return `$${amount}/week`;
  if (interval === "monthly") return `$${amount}/month`;
  return `$${amount}/year`;
}
